// Score → Song: project the score model onto the engraver's render model.
//
// Migration slice 2 (SPEC §Migration): the Score page renders
// scoreToSong(chartToScore(chart)) instead of chartToSong(chart), so the
// notation you see is derived from the real model. Behaviour matches
// chartToSong bar-for-bar (sections, chords, 1:1 measure→bar mapping —
// ScorePage's editing relies on the global bar index BEING the measure
// index), with one deliberate upgrade: the score model accounts for a bar's
// whole written time, so gaps engrave as real rests instead of blank staff.

import type { Bar, ChordEvent, MelodyNote, Section, Song } from '../song';
import { barVoices, uid } from '../song';
import { displayChordSymbol } from '../chart';
import { keyLabelOf } from './convert';
import { compareF, durationQuarters, frac, fractionToNumber, mulF } from './duration';
import type { SpelledPitch } from './pitch';
import { midiOf, noteNameOf, spellMidi, transposePitch } from './pitch';
import { voiceLength, voiceTiming } from './timing';
import type { KeySignature, Score, Transposition, VoiceItem } from './types';

function itemsUseStaff(
  items: VoiceItem[],
  homeStaff: number,
  targetStaff: number,
  notesOnly = false,
): boolean {
  return items.some((item) =>
    item.kind === 'tuplet'
      ? itemsUseStaff(item.items, homeStaff, targetStaff, notesOnly)
      : (item.staff ?? homeStaff) === targetStaff &&
        (!notesOnly || (item.kind === 'note' && item.notes.length > 0)),
  );
}

/** Derive the key signature a transposing player reads from concert-pitch
 * score truth. Kept beside the written-pitch projection so keyboard entry and
 * engraving cannot disagree about whether, for example, concert B-flat is a
 * written C for a B-flat instrument. */
export function writtenKeySignature(
  keySig: KeySignature | undefined,
  transposition: Transposition | undefined,
): KeySignature | undefined {
  if (!keySig || !transposition) return keySig;
  const step = keySig.tonic[0].toUpperCase() as SpelledPitch['step'];
  const alter = keySig.tonic.includes('#') ? 1 : keySig.tonic.includes('b') ? -1 : 0;
  const concertTonic: SpelledPitch = { step, alter, octave: 4 };
  let writtenTonic: SpelledPitch;
  try {
    writtenTonic = transposePitch(concertTonic, transposition);
  } catch {
    writtenTonic = spellMidi(midiOf(concertTonic) + transposition.semitones);
  }
  return { ...keySig, tonic: noteNameOf(writtenTonic) };
}

export function scoreToSong(score: Score): Song {
  return scoreToStaffSong(score, 0);
}

/** True when any part's voice carries a sung syllable — drives the lyric
 *  toggle's enablement (the control is inert on an unsung tune). */
export function scoreHasLyrics(score: Score): boolean {
  const itemHas = (it: { kind: string; items?: unknown[]; lyrics?: unknown[] }): boolean =>
    it.kind === 'tuplet'
      ? ((it.items ?? []) as { kind: string; lyrics?: unknown[] }[]).some(itemHas)
      : (it.lyrics?.length ?? 0) > 0;
  return score.parts.some((p) =>
    p.measures.some((m) => m.voices.some((v) => v.items.some(itemHas))),
  );
}

function itemsHaveNote(items: VoiceItem[]): boolean {
  return items.some((item) =>
    item.kind === 'tuplet'
      ? itemsHaveNote(item.items)
      : item.kind === 'note' && item.notes.length > 0,
  );
}

/** True when any part writes real notes on a bass-clef staff — the signal
 *  that the source carries its OWN bassline (whole-part playback sounds it,
 *  so the band's generated bass should stand down by default). Another
 *  part's line that is over by the bar the lead comes in is an intro, not the
 *  tune's bassline: a lead sheet's bass-clef intro becomes a Piano part
 *  (bassClefIntro.ts), and the band's bass still plays the head. */
export function scoreHasWrittenBass(score: Score): boolean {
  const lead = score.parts.find((part) => part.role === 'lead') ?? score.parts[0];
  const leadEnters =
    lead?.measures.findIndex((m) => m.voices.some((v) => itemsHaveNote(v.items))) ?? -1;
  return score.parts.some((part) =>
    part.staves.some((staffDef, si) => {
      if (staffDef.clef !== 'bass') return false;
      const bars = part.measures.flatMap((m, index) =>
        m.voices.some((v) => itemsUseStaff(v.items, v.staff ?? 0, si, true)) ? [index] : [],
      );
      if (bars.length === 0) return false;
      return part === lead || leadEnters < 0 || bars[bars.length - 1] > leadEnters;
    }),
  );
}

/** True when the tune's melody is played by a bass (Tricotism, Ladies in
 *  Mercedes): the part the band plays as its melody (the lead, or the first
 *  part with no role) is a bass instrument. That bass is the bass while it
 *  plays the head, and the band's generated line takes over in the choruses
 *  it does not (useBandArrangement's melodyPassSpans), so the Bass channel
 *  stays on rather than standing down for the whole tune. */
export function scoreMelodyIsBass(score: Score): boolean {
  const melody =
    score.parts.find((part) => part.role === 'lead') ??
    score.parts.find((part) => part.role === undefined);
  return melody?.instrument === 'bass' || melody?.role === 'bass';
}

/**
 * Project ONE staff of ONE part onto a renderable Song: the staff's first
 * voice becomes Bar.melody, its second Bar.melody2. Chord symbols ride only
 * on staff 0 (the harmony lane belongs at the top of the grand staff). This
 * is the grand-staff seam — the Score page engraves staff 1 (bass clef) as a
 * second aligned ScoreView — and the part seam: the page's part picker views
 * any part of a multi-part import (editing stays on the lead part).
 */
export function scoreToStaffSong(score: Score, staffIndex: number, partIndex = 0): Song {
  const sections: Section[] = [];
  const songMeter: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  let meter = songMeter;
  const lead = score.parts[partIndex] ?? score.parts[0];
  // A transposing part reads WRITTEN: the stored concert truth shifts up by
  // the part's transposition for display (clarinet in A: concert C4 reads
  // written Eb4). Sounding pitch is unchanged — the timeline compensates.
  const T = lead?.transposition;
  const written = (p: SpelledPitch): SpelledPitch => {
    if (!T) return p;
    try {
      return transposePitch(p, T);
    } catch {
      return spellMidi(midiOf(p) + T.semitones);
    }
  };

  score.form.forEach((fm, i) => {
    if (fm.timeSig) meter = fm.timeSig;
    if (sections.length === 0 || fm.section) {
      sections.push({
        id: `s${sections.length}`,
        name: fm.section ?? (i === 0 ? 'Main' : ''),
        bars: [],
      });
    }

    const chords: ChordEvent[] = [];
    const harmonyLabels: NonNullable<Bar['harmonyLabels']> = [];
    let beat = 1;
    for (const c of staffIndex === 0 ? fm.harmony : []) {
      const span = Number.isFinite(c.beats) && c.beats > 0 ? c.beats : 1;
      if (c.noChord) {
        harmonyLabels.push({ sourceId: c.id, beat, duration: span, text: c.symbol || 'N.C.' });
      } else if (c.chordType) {
        chords.push({
          sourceId: c.id,
          beat,
          duration: span,
          root: c.root,
          chordType: c.chordType,
          symbol: c.alternate
            ? `${displayChordSymbol(c)} (${displayChordSymbol(c.alternate)})`
            : displayChordSymbol(c),
          ...(c.targetTopNote ? { targetTopNote: c.targetTopNote } : {}),
          ...(Number.isInteger(c.preferredInversion)
            ? { preferredInversion: c.preferredInversion }
            : {}),
        });
      }
      beat += span;
    }

    const bar: Bar = {
      id: fm.id,
      beats: fm.pickupBeats ?? meter[0],
      chords,
      ...(harmonyLabels.length ? { harmonyLabels } : {}),
    };
    // After a meter change the bar's beats are in the new denominator.
    if (meter[0] !== songMeter[0] || meter[1] !== songMeter[1]) bar.timeSig = meter;
    // Barline decorations ride the form so every part draws them alike.
    if (fm.open === 'repeat' || fm.open === 'double') bar.openBarline = fm.open;
    if (fm.close === 'repeat' || fm.close === 'double' || fm.close === 'final')
      bar.closeBarline = fm.close;
    if (fm.ending != null) bar.ending = fm.ending;
    {
      // Overfull marking (Guitar Pro model): a duration edit may legally
      // leave a voice longer than the meter; the drawn bar carries the flag
      // so the renderer can show the error until the durations add up.
      // (pickup ?? meter beats) x 4/denominator, exact at 48 ticks/beat —
      // the model's own tick base, so any representable pickup is exact.
      const capacity = mulF(
        frac(Math.round((fm.pickupBeats ?? meter[0]) * 48), 48),
        frac(4, meter[1]),
      );
      const over = lead?.measures[i]?.voices.some(
        (voice) => compareF(voiceLength(voice), capacity) > 0,
      );
      if (over) bar.overfull = true;
    }
    if (fm.barRepeat != null) bar.barRepeat = fm.barRepeat;
    if (fm.staffText) bar.staffText = fm.staffText;
    if (fm.staffTextAbove != null) bar.staffTextAbove = fm.staffTextAbove;
    if (fm.directive) bar.directive = fm.directive;
    if (fm.feel?.label) bar.feel = fm.feel.label;
    if (fm.segno) bar.segno = true;
    if (fm.coda) bar.coda = true;
    // Song beats are the meter's denominator unit; timing is in quarters.
    const beatsPerQuarter = frac(meter[1], 4);
    const voiceToMelody = (
      voice: NonNullable<typeof lead>['measures'][number]['voices'][number],
    ) => {
      const melody: MelodyNote[] = [];
      // Written tuplet size + group identity by event id (a rest-led
      // sextuplet still reads 6; identity keeps two back-to-back quarter
      // triplets as two drawn brackets).
      const tupletOf = new Map<string, { actual: number; group: string }>();
      const collectTuplets = (
        items: typeof voice.items,
        enclosing?: { actual: number; group: string },
      ): void => {
        for (const item of items) {
          if (item.kind === 'tuplet') {
            // MelodyNote currently exposes one tuplet figure. For a nested
            // group the innermost written group owns the member's immediate
            // glyph and figure; its sounding duration still contains every
            // enclosing scale through voiceTiming.
            collectTuplets(item.items, { actual: item.actual, group: item.id });
          } else if (enclosing) {
            tupletOf.set(item.id, enclosing);
          }
        }
      };
      collectTuplets(voice.items);
      // Grace notes occupy no time (voiceTiming gives them zero duration, so
      // their start IS the principal's onset); collect a run and step its
      // beats back slightly so spacing draws it just left of the principal.
      let graceRun: {
        id: string;
        midi: number;
        spelledAs: string;
        written: number;
        sourceHeadIndex: number;
        chordNotes?: MelodyNote['chordNotes'];
      }[] = [];
      const flushGraces = (principalOnset: number | null) => {
        if (graceRun.length === 0) return;
        if (principalOnset !== null) {
          graceRun.forEach((g, k) => {
            melody.push({
              id: g.id,
              midi: g.midi,
              sourceHeadIndex: g.sourceHeadIndex,
              ...(g.chordNotes?.length ? { chordNotes: g.chordNotes } : {}),
              spelledAs: g.spelledAs,
              beat: principalOnset - 0.08 * (graceRun.length - k),
              duration: g.written,
              grace: true,
            });
          });
        }
        graceRun = []; // a dangling run (before a rest / bar end) doesn't draw
      };
      for (const t of voiceTiming(voice)) {
        if ((t.event.staff ?? voice.staff ?? 0) !== staffIndex) continue;
        const onset = 1 + fractionToNumber(mulF(t.start, beatsPerQuarter));
        if (t.event.grace) {
          if (t.event.kind === 'note' && t.event.notes.length > 0) {
            const head = t.event.notes.reduce((top, h) =>
              midiOf(h.pitch) > midiOf(top.pitch) ? h : top,
            );
            const headW = written(head.pitch);
            graceRun.push({
              id: t.event.id,
              midi: midiOf(headW),
              sourceHeadIndex: t.event.notes.indexOf(head),
              spelledAs: noteNameOf(headW),
              chordNotes: t.event.notes.flatMap((h, index) => {
                if (h === head) return [];
                const w = written(h.pitch);
                return [
                  {
                    midi: midiOf(w),
                    spelledAs: noteNameOf(w),
                    sourceHeadIndex: index,
                    ...(h.tie ? { tie: h.tie } : {}),
                  },
                ];
              }),
              written: fractionToNumber(mulF(durationQuarters(t.event.duration), beatsPerQuarter)),
            });
          }
          continue;
        }
        const duration = fractionToNumber(mulF(t.duration, beatsPerQuarter));
        const writtenDuration = fractionToNumber(
          mulF(durationQuarters(t.event.duration), beatsPerQuarter),
        );
        const tuplet = tupletOf.get(t.event.id);
        if (t.event.kind === 'rest') {
          flushGraces(null);
          melody.push({
            id: t.event.id,
            midi: 60,
            beat: onset,
            duration,
            rest: true,
            // A cue passage's rests are cue-size too.
            ...(t.event.cue ? { cue: true } : {}),
            ...(t.event.dynamic ? { dynamic: t.event.dynamic } : {}),
            ...(t.event.words ? { words: t.event.words } : {}),
            ...(t.event.wordsBelow ? { wordsBelow: t.event.wordsBelow } : {}),
            // A rest inside a tuplet keeps the group's identity so an
            // unbeamed bracket spans it (…r4 a b… is one triplet).
            ...(tuplet
              ? {
                  writtenDuration,
                  tuplet: tuplet.actual,
                  tupletGroup: tuplet.group,
                }
              : {}),
          });
          continue;
        }
        if (t.event.slash) {
          flushGraces(null);
          melody.push({
            id: t.event.id,
            midi: 0,
            beat: onset,
            duration,
            slash: t.event.slash,
            ...(t.event.cue ? { cue: true } : {}),
            ...(t.event.words ? { words: t.event.words } : {}),
            ...(t.event.wordsBelow ? { wordsBelow: t.event.wordsBelow } : {}),
            ...(t.event.articulations?.length ? { articulations: t.event.articulations } : {}),
            ...(t.event.dynamic ? { dynamic: t.event.dynamic } : {}),
            ...(tuplet
              ? { writtenDuration, tuplet: tuplet.actual, tupletGroup: tuplet.group }
              : {}),
          });
          continue;
        }
        if (t.event.notes.length === 0) continue;
        flushGraces(onset);
        // Top notehead carries the line (ties, selection, lead targets); the
        // rest of a chord event rides along as chordNotes for the engraver.
        const head = t.event.notes.reduce((top, h) =>
          midiOf(h.pitch) > midiOf(top.pitch) ? h : top,
        );
        const headW = written(head.pitch);
        const lower = t.event.notes
          .filter((h) => h !== head)
          .map((h) => {
            const w = written(h.pitch);
            return {
              midi: midiOf(w),
              spelledAs: noteNameOf(w),
              sourceHeadIndex: t.event.notes.indexOf(h),
              ...(h.tie ? { tie: h.tie } : {}),
            };
          })
          .sort((a, b) => b.midi - a.midi);
        melody.push({
          id: t.event.id,
          midi: midiOf(headW),
          sourceHeadIndex: t.event.notes.indexOf(head),
          spelledAs: noteNameOf(headW),
          beat: onset,
          duration,
          ...(head.tie ? { tie: head.tie } : {}),
          ...(t.event.cue ? { cue: true } : {}),
          ...(tuplet
            ? {
                writtenDuration,
                tuplet: tuplet.actual,
                tupletGroup: tuplet.group,
              }
            : {}),
          ...(t.event.articulations?.length ? { articulations: t.event.articulations } : {}),
          ...(t.event.ornaments?.length ? { ornaments: t.event.ornaments } : {}),
          ...(t.event.dynamic ? { dynamic: t.event.dynamic } : {}),
          ...(t.event.words ? { words: t.event.words } : {}),
          ...(t.event.wordsBelow ? { wordsBelow: t.event.wordsBelow } : {}),
          ...(t.event.lyrics?.length ? { lyrics: t.event.lyrics } : {}),
          ...(lower.length > 0 ? { chordNotes: lower } : {}),
        });
      }
      return melody;
    };
    const staffVoices = (lead?.measures[i]?.voices ?? []).filter((v) =>
      itemsUseStaff(v.items, v.staff ?? 0, staffIndex),
    );
    if (staffVoices[0]) {
      const melody = voiceToMelody(staffVoices[0]);
      // A form-measure fermata draws over the last sounding note, or the
      // last rest in a silent bar. A held silence must not invent a pitch.
      if (fm.fermata) {
        const last = [...melody].reverse().find((n) => !n.rest) ?? melody[melody.length - 1];
        if (last) last.ornaments = [...(last.ornaments ?? []), 'fermata'];
      }
      if (melody.length > 0) bar.melody = melody;
    }
    if (staffVoices[1]) {
      const melody2 = voiceToMelody(staffVoices[1]);
      if (melody2.length > 0) bar.melody2 = melody2;
    }
    // Voices 3+ (the Telemann ensemble staves carry three): positional, so
    // voice index survives; trailing empties trim away.
    if (staffVoices.length > 2) {
      const more = staffVoices.slice(2).map(voiceToMelody);
      while (more.length > 0 && more[more.length - 1].length === 0) more.pop();
      if (more.some((v) => v.length > 0)) bar.melodyMore = more;
    }
    sections[sections.length - 1].bars.push(bar);
  });

  if (sections.length === 0) sections.push({ id: 's0', name: 'Main', bars: [] });

  // Slurs whose BOTH endpoints render on this staff (graces render too, so a
  // turn's slur onto its principal draws; a slur to another staff doesn't
  // draw here). Rendered melody-note ids ARE the event ids spanners reference.
  const renderedIds = new Set<string>();
  for (const sec of sections) {
    for (const bar of sec.bars) {
      for (const n of barVoices(bar).flat()) {
        if (!n.rest) renderedIds.add(n.id);
      }
    }
  }
  const slurs = (score.spanners ?? [])
    .filter(
      (sp) =>
        sp.type === 'slur' && renderedIds.has(sp.fromEventId) && renderedIds.has(sp.toEventId),
    )
    .map((sp) => ({
      fromId: sp.fromEventId,
      toId: sp.toEventId,
      ...(sp.side ? { side: sp.side } : {}),
    }));
  const hairpins = (score.spanners ?? [])
    .filter(
      (sp) =>
        (sp.type === 'cresc' || sp.type === 'dim') &&
        renderedIds.has(sp.fromEventId) &&
        renderedIds.has(sp.toEventId),
    )
    .map((sp) => ({
      fromId: sp.fromEventId,
      toId: sp.toEventId,
      kind: sp.type as 'cresc' | 'dim',
    }));

  // The written key for a transposing part: move the concert tonic by T.
  const writtenKeySig = writtenKeySignature(score.form[0]?.keySig, T);

  return {
    id: score.id ?? uid('song'),
    title: score.title,
    key: keyLabelOf(writtenKeySig),
    timeSignature: score.form[0]?.timeSig ?? [4, 4],
    sections,
    ...(slurs.length > 0 ? { slurs } : {}),
    ...(hairpins.length > 0 ? { hairpins } : {}),
    ...(score.tempoText ? { tempoText: score.tempoText } : {}),
    // The book prints the metronome mark beside the words ("Allegro (♩ = 130)")
    // — but only when there ARE words: a bare number is transport state, not
    // part of the engraving.
    ...(score.tempoText && score.tempo ? { tempoBpm: score.tempo } : {}),
  };
}
